import React, { useRef, useState } from 'react';
import axios from 'axios';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// Firmware distribution/management is SaaS-only; its API functions live in the
// feature module (trial-aliased to no-ops) and the UI is gated via useFeature.
import {
  fetchReleasedFirmwareImages,
  fetchSystemImages,
  setDefaultBoot,
  type FirmwareImage,
  type SystemImage,
} from '@/features/firmware';
import { useFeature } from '@/hooks/useCapabilities.ts';
import { IS_TRIAL } from '@/lib/edition.ts';
import {
  AlertCircle,
  CheckCircle2,
  ArrowUpCircle,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Download,
  MoreHorizontal,
  Pencil,
  Plus,
  PowerOff,
  Router,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  XCircle,
} from 'lucide-react';
import { config } from '../../config';
import { useAuth } from '../../hooks/useAuth';
import { tokenIsLive, useCancelProvisioningToken, useProvisioningTokens } from '@/hooks/useProvisioningTokens.ts';
import { useConfirm } from '@/hooks/useConfirm';
import { LocalNetworkScanPanel } from '../../components/gateways/LocalNetworkScanPanel';
import { AddGatewayDialog } from '../../components/gateways/AddGatewayDialog';
import { ApplianceConsoleCard } from '../../components/gateways/ApplianceConsoleCard';
import { SetupFileCard } from '../../components/gateways/SetupFileCard';
import { useGatewayClaim } from '../../hooks/useGatewayClaim';
import { usePermission } from '../../hooks/usePermission';
import { GatewayImageDownloads } from '../../components/gateways/GatewayImageDownloads';
import { classifyFirmwareFile } from '../../components/gateways/firmwareArtifacts';
import ResourceTags from '../../components/ResourceTags';
import ConfirmationModal from '../../components/ConfirmationModal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppSheet } from '@/components/ui/app-sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DockerDeployInstructions } from '@/components/gateways/DockerDeployInstructions';
import { GatewaySoftwareVersion } from '@/components/gateways/GatewaySoftwareVersion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { withAccount } from '@/lib/activeAccount';

interface GatewayListResponse {
  gateways: Gateway[];
}

interface Gateway {
  id: string;
  name: string;
  description: string | null;
  status: string;
  ipAddress: string;
  regionId: string;
  regionName: string;
  divisionId: string;
  divisionName: string;
  softwareVersion: string | null;
  /** 'container' | 'vyos_iso' — what the gateway reported it is. */
  deploymentKind: string;
  architecture: string | null;
  dbEncryptionPolicy: string | null;
  dbEncryptionEffectivePolicy: string | null;
  dbEncryptionSlots: string[];
  dbEncryptionTpm: boolean | null;
  dbEncryptionReportedAt: string | null;
  dbEncryptionCompliant: boolean | null;
}

const DB_ENCRYPTION_POLICY_LABELS: Record<string, string> = {
  local_file: 'Local file key',
  saas_assisted: 'SaaS-assisted unlock',
  require_tpm: 'Require TPM',
};

function dbEncryptionPolicyLabel(policy: string | null | undefined): string {
  if (!policy) return 'Unknown';
  return DB_ENCRYPTION_POLICY_LABELS[policy] ?? policy;
}

function StatusBadge({ status }: { status: string }) {
  const s = status.toLowerCase();
  if (s === 'online') {
    return (
      <Badge className="bg-sev-4/15 text-sev-4 border-0 gap-1 font-normal capitalize">
        <CheckCircle2 className="w-3 h-3" />
        {status}
      </Badge>
    );
  }
  if (s === 'offline') {
    return (
      <Badge className="bg-sev-1/15 text-sev-1 border-0 gap-1 font-normal capitalize">
        <XCircle className="w-3 h-3" />
        {status}
      </Badge>
    );
  }
  // Amber, unlike the neutral grey an `unknown` *device* gets. A device's
  // observer can lapse while the device is perfectly healthy; a gateway is the
  // observer, so one that has not reported is a gap in monitoring itself and
  // wants attention — the same reading the MSP rollup gives never-seen gateways.
  return (
    <Badge className="bg-sev-3/15 text-foreground border-0 gap-1 font-normal capitalize">
      <CircleHelp className="w-3 h-3" />
      {status}
    </Badge>
  );
}

interface UnlockAttempt {
  createdAt: string;
  sourceIp: string | null;
  success: boolean;
  detail: string | null;
}

// DB-encryption status for a gateway (gateway docs/GatewayDbEncryption.md §3):
// effective policy, reported keyslots, compliance, and the pre-auth unlock
// audit trail — the log a stolen-disk unlock attempt is forced to write to.
function GatewayEncryptionCard({ gateway }: { gateway: Gateway }) {
  const backendApi = config.backendApi;
  const reported = gateway.dbEncryptionReportedAt;
  const compliant = gateway.dbEncryptionCompliant;
  const showsSaas =
    gateway.dbEncryptionEffectivePolicy === 'saas_assisted' ||
    gateway.dbEncryptionSlots.includes('saas');

  const { data: attempts } = useQuery<UnlockAttempt[]>({
    queryKey: ['gateway-unlock-attempts', gateway.id, backendApi],
    queryFn: async () => {
      const res = await axios.get<UnlockAttempt[]>(
        `${backendApi}/api/gateways/${gateway.id}/unlock-attempts`,
        { withCredentials: true },
      );
      return res.data;
    },
    enabled: showsSaas,
  });

  return (
    <div className="rounded-md border border-border-subtle bg-card p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium">
        {compliant === false ? (
          <ShieldAlert className="w-4 h-4 text-sev-2" />
        ) : (
          <ShieldCheck className="w-4 h-4 text-sev-4" />
        )}
        Database encryption
        {compliant === false ? (
          <Badge className="bg-sev-2/15 text-sev-2 border-0 font-normal">
            Non-compliant
          </Badge>
        ) : compliant === true ? (
          <Badge className="bg-sev-4/15 text-sev-4 border-0 font-normal">
            Compliant
          </Badge>
        ) : null}
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <dt className="text-muted-foreground">Effective policy</dt>
        <dd>
          {dbEncryptionPolicyLabel(gateway.dbEncryptionEffectivePolicy)}
          {gateway.dbEncryptionPolicy ? null : (
            <span className="text-muted-foreground"> (inherited)</span>
          )}
        </dd>
        <dt className="text-muted-foreground">Key storage</dt>
        <dd>
          {gateway.dbEncryptionSlots.length > 0
            ? gateway.dbEncryptionSlots.join(', ')
            : '—'}
        </dd>
        <dt className="text-muted-foreground">TPM present</dt>
        <dd>
          {gateway.dbEncryptionTpm === null
            ? '—'
            : gateway.dbEncryptionTpm
              ? 'Yes'
              : 'No'}
        </dd>
        <dt className="text-muted-foreground">Last reported</dt>
        <dd>
          {reported && !isNaN(parseISO(reported).getTime()) ? (
            <span title={format(parseISO(reported), 'PPpp')}>
              {formatDistanceToNow(parseISO(reported), { addSuffix: true })}
            </span>
          ) : (
            'Never'
          )}
        </dd>
      </dl>
      {showsSaas && attempts && attempts.length > 0 ? (
        <div className="pt-1">
          <p className="text-xs font-medium text-muted-foreground mb-1">
            Recent unlock attempts
          </p>
          <ul className="space-y-0.5 text-xs">
            {attempts.slice(0, 5).map((a, i) => (
              <li key={i} className="flex items-center gap-2">
                {a.success ? (
                  <CheckCircle2 className="w-3 h-3 text-sev-4 shrink-0" />
                ) : (
                  <XCircle className="w-3 h-3 text-sev-1 shrink-0" />
                )}
                <span className="text-muted-foreground">
                  {a.sourceIp || 'unknown'}
                </span>
                {a.detail ? (
                  <span className="text-muted-foreground">· {a.detail}</span>
                ) : null}
                <span className="text-muted-foreground ml-auto">
                  {!isNaN(parseISO(a.createdAt).getTime())
                    ? formatDistanceToNow(parseISO(a.createdAt), { addSuffix: true })
                    : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export default function Gateways() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organizationId = user?.organizationId;

  const [isEditModalActive, setIsEditModalActive] = useState(false);
  const [editingGateway, setEditingGateway] = useState<Gateway | null>(null);
  const [deletingGateway, setDeletingGateway] = useState<Gateway | null>(null);
  const [showInactiveTokens, setShowInactiveTokens] = useState(false);
  const [isTokenSectionExpanded, setIsTokenSectionExpanded] = useState(false);
  const [expandedGateways, setExpandedGateways] = useState<Set<string>>(new Set());
  const [versionGateway, setVersionGateway] = useState<Gateway | null>(null);
  const [selectedFirmwareId, setSelectedFirmwareId] = useState('');
  const [rebootConfirmGateway, setRebootConfirmGateway] = useState<Gateway | null>(null);
  const [isDownloadModalActive, setIsDownloadModalActive] = useState(false);
  const [isAddGatewayOpen, setIsAddGatewayOpen] = useState(false);
  const scanPanelRef = useRef<HTMLDivElement>(null);
  const { startScan } = useGatewayClaim();
  // `gateway:create` is what both add-paths enforce server-side (claim_gateway
  // and the token endpoint), so offering either without it just produces a 401.
  const canAddGateways = usePermission('gateway:create');
  const canCancelTokens = usePermission('gateway_provisioning_token:cancel');
  const cancelToken = useCancelProvisioningToken();
  const { confirm, confirmDialog } = useConfirm();
  const canUpdateGateways = usePermission('gateway:update');

  /**
   * The Add gateway dialog hands the local-network path back to the page: the
   * click there is the user gesture that may raise the permission prompt, and
   * the panel below already owns the results table and claim form.
   */
  const handleChooseScan = () => {
    startScan();
    scanPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const backendApi = config.backendApi;

  const fetchGateways = async (): Promise<Gateway[]> => {
    const response = await axios.get<GatewayListResponse>(`${backendApi}/api/gateways`, {
      withCredentials: true,
    });
    return response.data.gateways;
  };

  const {
    data: gateways,
    isLoading: isLoadingGateways,
    isError: isErrorGateways,
    error: gatewayError,
  } = useQuery<Gateway[]>({
    queryKey: ['gateways', organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId is required to fetch gateways.');
      return fetchGateways();
    },
    enabled: !!organizationId,
  });

  const {
    data: provisioningTokens,
    isLoading: isLoadingTokens,
    isError: isErrorTokens,
    error: tokenError,
  } = useProvisioningTokens(organizationId);

  const firmwareEnabled = useFeature('firmware');

  const { data: firmwareImages } = useQuery<FirmwareImage[]>({
    queryKey: ['firmware-images-released', backendApi],
    queryFn: fetchReleasedFirmwareImages,
    enabled: !!organizationId,
  });

  const {
    data: systemImages,
    isLoading: isLoadingSystemImages,
    refetch: refetchSystemImages,
  } = useQuery<SystemImage[]>({
    queryKey: ['system-images', versionGateway?.id, backendApi],
    queryFn: () => fetchSystemImages(versionGateway?.id ?? ''),
    // VyOS appliances only — containers have no VyOS image API.
    enabled: !!versionGateway && versionGateway.deploymentKind === 'vyos_iso',
  });

  const setDefaultBootMutation = useMutation<
    { success: boolean; message: string },
    Error,
    { gatewayId: string; imageName: string }
  >({
    mutationFn: setDefaultBoot,
    onSuccess: () => { void refetchSystemImages(); },
  });

  const rebootGatewayMutation = useMutation<
    { success: boolean; message: string },
    Error,
    { gatewayId: string }
  >({
    mutationFn: async (data) => {
      const response = await axios.post<{ success: boolean; message: string }>(
        `${backendApi}/api/gateways/${data.gatewayId}/reboot`,
        {},
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: () => {
      setRebootConfirmGateway(null);
      void queryClient.invalidateQueries({ queryKey: ['gateways'] });
    },
  });

  // Active is "can still add a gateway": a setup file's token goes on claiming
  // after its first, so having claimed doesn't hide it.
  const filteredTokens = provisioningTokens?.filter((token) => showInactiveTokens || tokenIsLive(token)) || [];

  const hiddenTokensCount = (provisioningTokens?.length ?? 0) - filteredTokens.length;


  const updateGatewayMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    Error,
    { gatewayId: string; name: string; description: string | null; dbEncryptionPolicy: string }
  >({
    mutationFn: async (gatewayData) => {
      await axios.put(
        `${backendApi}/api/gateways/${gatewayData.gatewayId}`,
        gatewayData,
        { withCredentials: true },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gateways'] });
      closeEditModal();
    },
    onError: (error) => {
      console.error('Failed to update gateway:', error);
    },
  });

  const deleteGatewayMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    Error,
    { gatewayId: string }
  >({
    mutationFn: async (gatewayData) => {
      await axios.delete(`${backendApi}/api/gateways/${gatewayData.gatewayId}`, {
        withCredentials: true,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gateways'] });
      setDeletingGateway(null);
    },
    onError: (error) => {
      console.error('Failed to delete gateway:', error);
    },
  });

  const openEditModal = (gateway: Gateway) => {
    setEditingGateway(gateway);
    setIsEditModalActive(true);
  };

  const closeEditModal = () => {
    setEditingGateway(null);
    setIsEditModalActive(false);
  };

  const handleDeleteGateway = () => {
    if (deletingGateway) deleteGatewayMutation.mutate({ gatewayId: deletingGateway.id });
  };

  const handleUpdateGatewaySubmit = (event: React.SubmitEvent) => {
    event.preventDefault();
    if (editingGateway) {
      updateGatewayMutation.mutate({
        gatewayId: editingGateway.id,
        name: editingGateway.name,
        description: editingGateway.description,
        dbEncryptionPolicy: editingGateway.dbEncryptionPolicy ?? 'inherit',
      });
    }
  };

  const toggleGatewayExpansion = (gatewayId: string) => {
    const newExpanded = new Set(expandedGateways);
    if (expandedGateways.has(gatewayId)) newExpanded.delete(gatewayId);
    else newExpanded.add(gatewayId);
    setExpandedGateways(newExpanded);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Gateways"
        primary={
          <div className="flex flex-wrap items-center gap-2">
            {/* Demoted from a primary button: its job is fetching image files
                (including older ISOs for upgrades), not adding a gateway.
                Presenting it alongside "Add gateway" is what made the two read
                as alternative halves of one flow. */}
            <Button
              variant="ghost"
              onClick={() => { setIsDownloadModalActive(true); }}
              className="gap-1.5"
            >
              <Download className="w-4 h-4" />
              Downloads
            </Button>
            <Button
              onClick={() => { setIsAddGatewayOpen(true); }}
              disabled={!canAddGateways}
              title={canAddGateways ? undefined : 'You do not have permission to add gateways.'}
              className="gap-1.5"
            >
              <Plus className="w-4 h-4" />
              Add gateway
            </Button>
          </div>
        }
      />

      {isLoadingGateways ? <p className="text-sm text-muted-foreground">Loading gateways…</p> : null}
      {isErrorGateways ? (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load gateways: {gatewayError.message || 'An unknown error occurred.'}
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Scanning is the zero-config path, so it leads. With no gateways yet it
          is the whole point of the page; once there are some it collapses to a
          strip above the table. Either way it sits *above* the fold — burying
          it under the table meant nobody ever granted the permission it needs,
          which in turn meant the background watch never ran for anyone. */}
      {!isLoadingGateways && !isErrorGateways && canAddGateways ? (
        <div ref={scanPanelRef}>
          <LocalNetworkScanPanel
            variant={!gateways || gateways.length === 0 ? 'hero' : 'compact'}
          />
        </div>
      ) : null}

      {!isLoadingGateways && !isErrorGateways && (!gateways || gateways.length === 0) ? (
        <EmptyState
          variant="no-data"
          icon={Router}
          title="No gateways yet"
          description={
            canAddGateways
              ? 'Gateways connect this SaaS to your network. Scan for one above, or walk through the other ways to add one.'
              : 'Gateways connect this SaaS to your network. You do not have permission to add one — ask an administrator.'
          }
          action={
            <Button
              variant="outline"
              onClick={() => { setIsAddGatewayOpen(true); }}
              disabled={!canAddGateways}
              className="gap-1.5"
            >
              <Plus className="w-4 h-4" />
              Add gateway
            </Button>
          }
        />
      ) : null}

      {!isLoadingGateways && !isErrorGateways && gateways && gateways.length > 0 ? (
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm">
            <thead className="border-b border-border-subtle">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2 font-medium w-8" />
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Description</th>
                <th className="px-3 py-2 font-medium">State</th>
                <th className="px-3 py-2 font-medium">IP address</th>
                <th className="px-3 py-2 font-medium">Version</th>
                <th className="px-3 py-2 font-medium">Region</th>
                <th className="px-3 py-2 font-medium">Division</th>
                <th className="px-3 py-2 font-medium w-12" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {gateways.map((gateway) => {
                const isExpanded = expandedGateways.has(gateway.id);
                return (
                  <React.Fragment key={gateway.id}>
                    <tr className="hover:bg-accent/30">
                      <td className="px-3 py-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleGatewayExpansion(gateway.id);
                          }}
                          aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5" />
                          )}
                        </Button>
                      </td>
                      <td className="px-3 py-2 font-medium">{gateway.name || '[Unnamed]'}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {gateway.description || '—'}
                      </td>
                      <td className="px-3 py-2"><StatusBadge status={gateway.status} /></td>
                      <td className="px-3 py-2">
                        <code className="font-mono text-xs">{gateway.ipAddress}</code>
                      </td>
                      <td className="px-3 py-2">
                        {/* One version per gateway: the same release builds
                            the container image, the .deb and the VyOS ISO. */}
                        {gateway.softwareVersion ? (
                          <Badge variant="secondary" className="font-normal">
                            {gateway.softwareVersion}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">{gateway.regionName}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {gateway.divisionName || '—'}
                      </td>
                      <td className="px-3 py-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Actions for ${gateway.name || 'gateway'}`}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => { openEditModal(gateway); }}
                              className="gap-2"
                            >
                              <Pencil className="w-4 h-4" />
                              Edit
                            </DropdownMenuItem>
                            {firmwareEnabled ? (
                              <DropdownMenuItem
                                onClick={() => {
                                  setVersionGateway(gateway);
                                  setSelectedFirmwareId('');
                                  setDefaultBootMutation.reset();
                                  rebootGatewayMutation.reset();
                                }}
                                className="gap-2"
                              >
                                <ArrowUpCircle className="w-4 h-4" />
                                Software version
                              </DropdownMenuItem>
                            ) : null}
                            <DropdownMenuItem
                              onClick={() => { setDeletingGateway(gateway); }}
                              className="gap-2 text-sev-1 focus:text-sev-1"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                    {isExpanded ? (
                      <tr>
                        <td colSpan={9} className="bg-muted/30 px-6 py-4 space-y-4">
                          <ResourceTags resourceType="gateway" resourceId={gateway.id} />
                          <GatewayEncryptionCard gateway={gateway} />
                          {canUpdateGateways && gateway.deploymentKind === 'vyos_iso' ? (
                            <ApplianceConsoleCard gatewayId={gateway.id} />
                          ) : null}
                          {canUpdateGateways && gateway.deploymentKind === 'vyos_iso' ? (
                            <SetupFileCard gatewayId={gateway.id} gatewayName={gateway.name} />
                          ) : null}
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Provisioning Tokens Section */}
      <section className="rounded-lg border border-border bg-card p-4 mt-6">
        <button
          type="button"
          className="w-full flex items-center justify-between text-left"
          onClick={() => { setIsTokenSectionExpanded(!isTokenSectionExpanded); }}
        >
          <h2 className="text-base font-semibold flex items-center gap-2">
            {isTokenSectionExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            Gateway provisioning tokens
            {!isLoadingTokens ? (
              <Badge variant="secondary" className="font-normal">
                {filteredTokens.length} active
              </Badge>
            ) : null}
          </h2>
        </button>

        {isTokenSectionExpanded ? (
          <div className="mt-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer mb-3">
              <Checkbox
                checked={showInactiveTokens}
                onCheckedChange={(v) => { setShowInactiveTokens(v === true); }}
              />
              Show inactive tokens
              {!showInactiveTokens && hiddenTokensCount > 0 ? (
                <Badge variant="secondary" className="font-normal text-[10px]">
                  {hiddenTokensCount} hidden
                </Badge>
              ) : null}
            </label>

            {isLoadingTokens ? (
              <p className="text-sm text-muted-foreground">Loading provisioning tokens…</p>
            ) : null}
            {isErrorTokens ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Failed to load provisioning tokens:{' '}
                  {tokenError.message || 'An unknown error occurred.'}
                </AlertDescription>
              </Alert>
            ) : null}

            {!isLoadingTokens && !isErrorTokens && filteredTokens.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {showInactiveTokens
                  ? 'No provisioning tokens found.'
                  : 'No active provisioning tokens found.'}
              </p>
            ) : null}

            {!isLoadingTokens && !isErrorTokens && filteredTokens.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-border-subtle">
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="px-3 py-2 font-medium">Token</th>
                      <th className="px-3 py-2 font-medium">Name</th>
                      <th className="px-3 py-2 font-medium">Type</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium">Description</th>
                      <th className="px-3 py-2 font-medium">Expires</th>
                      <th className="px-3 py-2 font-medium"><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-subtle">
                    {filteredTokens.map((token) => (
                      <tr key={token.id}>
                        <td className="px-3 py-2">
                          <code className="font-mono text-xs">{token.token}</code>
                        </td>
                        <td className="px-3 py-2 text-xs">{token.name || '—'}</td>
                        <td className="px-3 py-2 text-xs capitalize">{token.gatewayType}</td>
                        <td className="px-3 py-2 text-xs">
                          <span className="capitalize">{token.status}</span>
                          {token.maxUses === null ? (
                            <span className="block text-muted-foreground">
                              Any number of gateways · {token.uses} so far
                            </span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {token.description || '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {(() => {
                            if (!token.expiresAt) return '—';
                            const parsedDate = parseISO(token.expiresAt);
                            if (isNaN(parsedDate.getTime())) return '—';
                            return (
                              <span title={format(parsedDate, 'PPpp')}>
                                {formatDistanceToNow(parsedDate, { addSuffix: true })}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {canCancelTokens && tokenIsLive(token) ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={cancelToken.isPending}
                              onClick={() => {
                                void (async () => {
                                  const ok = await confirm({
                                    title: 'Cancel this token?',
                                    message:
                                      token.maxUses === null
                                        ? 'No more gateways can be added with it, including from setup files that carry it. Gateways it already added stay.'
                                        : 'A technician part-way through provisioning with it will have to start again — the token string cannot be recovered.',
                                    confirmText: 'Cancel token',
                                    isDanger: true,
                                  });
                                  if (ok) cancelToken.mutate(token.id);
                                })();
                              }}
                            >
                              Cancel
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}
      </section>

      {/* Add Token Sheet */}
      {/* Download Gateway Dialog */}
      <Dialog
        open={isDownloadModalActive}
        onOpenChange={(open) => { if (!open) setIsDownloadModalActive(false); }}
      >
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Downloads &amp; deploy reference</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Images and ready-to-run commands for gateways you&apos;re deploying by hand,
            plus older ISOs for upgrades. To add a new gateway, use{' '}
            <strong>Add gateway</strong> — it issues the token for you.
          </p>

          <DockerDeployInstructions
            tokens={provisioningTokens ?? []}
            virtualMachineSlot={<GatewayImageDownloads firmwareImages={firmwareImages} />}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Gateway Sheet */}
      <AppSheet
        open={isEditModalActive}
        onOpenChange={(open) => { if (!open) closeEditModal(); }}
        title="Edit gateway"
        size="md"
      >
        <AppSheet.Body>
          {updateGatewayMutation.isError ? (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {updateGatewayMutation.error.message || 'Failed to update gateway.'}
              </AlertDescription>
            </Alert>
          ) : null}
          {editingGateway ? (
            <form id="edit-gateway-form" onSubmit={handleUpdateGatewaySubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="eg-name">Name</Label>
                <Input
                  id="eg-name"
                  type="text"
                  value={editingGateway.name}
                  onChange={(e) => {
                    setEditingGateway({ ...editingGateway, name: e.target.value });
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="eg-desc">Description</Label>
                <Textarea
                  id="eg-desc"
                  value={editingGateway.description || ''}
                  onChange={(e) => {
                    setEditingGateway({ ...editingGateway, description: e.target.value });
                  }}
                />
              </div>
              {/* The trial edition pins local_file (the bundled backend is the
                  "SaaS", so saas_assisted is circular and require_tpm can't be
                  met in a container). Hide the control; the status card below
                  still shows the effective policy. */}
              {IS_TRIAL ? null : (
                <div className="space-y-1.5">
                  <Label htmlFor="eg-encryption">Database encryption policy</Label>
                  <Select
                    value={editingGateway.dbEncryptionPolicy ?? 'inherit'}
                    onValueChange={(value) => {
                      setEditingGateway({
                        ...editingGateway,
                        dbEncryptionPolicy: value === 'inherit' ? null : value,
                      });
                    }}
                  >
                    <SelectTrigger id="eg-encryption">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inherit">
                        Inherit organization default
                      </SelectItem>
                      <SelectItem value="local_file">Local file key</SelectItem>
                      <SelectItem value="saas_assisted">SaaS-assisted unlock</SelectItem>
                      <SelectItem value="require_tpm">Require TPM</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Controls how the gateway&apos;s local credential database is
                    encrypted at rest. The gateway reconciles its key storage to
                    this policy on its next check-in.
                  </p>
                </div>
              )}
            </form>
          ) : null}
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            type="button"
            onClick={closeEditModal}
            disabled={updateGatewayMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-gateway-form"
            disabled={updateGatewayMutation.isPending}
          >
            {updateGatewayMutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <AddGatewayDialog
        open={isAddGatewayOpen}
        onOpenChange={setIsAddGatewayOpen}
        firmwareImages={firmwareImages}
        onChooseScan={handleChooseScan}
      />
      {confirmDialog}

      <ConfirmationModal
        isActive={!!deletingGateway}
        title="Delete gateway"
        message={
          deletingGateway
            ? `Are you sure you want to delete the gateway "${deletingGateway.name || '[Unnamed]'}"? This action cannot be undone.`
            : ''
        }
        confirmText="Delete gateway"
        cancelText="Cancel"
        onConfirm={handleDeleteGateway}
        onCancel={() => { setDeletingGateway(null); }}
        isLoading={deleteGatewayMutation.isPending}
        isDanger={true}
      />

      {/* Software Version Dialog — one control for every gateway: update
          policy + pinning for containers and VMs alike. VyOS appliances
          additionally get image management: installed images, manual
          install and reboot. */}
      <Dialog
        open={!!versionGateway}
        onOpenChange={(open) => { if (!open) setVersionGateway(null); }}
      >
        <DialogContent
          className={cn(
            'max-h-[90vh] overflow-y-auto',
            versionGateway?.deploymentKind === 'vyos_iso' ? 'max-w-3xl' : 'max-w-lg',
          )}
        >
          <DialogHeader>
            <DialogTitle>Software version — {versionGateway?.name || '[Unnamed]'}</DialogTitle>
          </DialogHeader>
          {versionGateway ? (
            <div className="space-y-4">
              <GatewaySoftwareVersion gatewayId={versionGateway.id} />
              {versionGateway.deploymentKind === 'vyos_iso' ? (
                <>
                  <Separator />

              {setDefaultBootMutation.isError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {setDefaultBootMutation.error.message || 'Failed to set default boot image.'}
                  </AlertDescription>
                </Alert>
              ) : null}
              {setDefaultBootMutation.isSuccess ? (
                <Alert
                  className={cn(
                    setDefaultBootMutation.data.success
                      ? 'border-sev-4/40 bg-sev-4/10'
                      : 'border-sev-3/40 bg-sev-3/10',
                  )}
                >
                  <AlertDescription>{setDefaultBootMutation.data.message}</AlertDescription>
                </Alert>
              ) : null}
              {rebootGatewayMutation.isError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {rebootGatewayMutation.error.message || 'Failed to reboot gateway.'}
                  </AlertDescription>
                </Alert>
              ) : null}
              {rebootGatewayMutation.isSuccess ? (
                <Alert className="border-sev-4/40 bg-sev-4/10">
                  <AlertDescription>{rebootGatewayMutation.data.message}</AlertDescription>
                </Alert>
              ) : null}

              <h4 className="text-sm font-semibold">Images on this appliance</h4>
              {isLoadingSystemImages ? (
                <p className="text-sm text-muted-foreground">Loading system images…</p>
              ) : null}
              {!isLoadingSystemImages && (!systemImages || systemImages.length === 0) ? (
                <Alert className="border-sev-3/40 bg-sev-3/10">
                  <AlertDescription>
                    No system images found. The gateway may not support image management.
                  </AlertDescription>
                </Alert>
              ) : null}
              {!isLoadingSystemImages && systemImages && systemImages.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-border-subtle">
                      <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="px-3 py-2 font-medium">Image</th>
                        <th className="px-3 py-2 font-medium">Status</th>
                        <th className="px-3 py-2 font-medium w-32 text-right" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-subtle">
                      {systemImages.map((image) => (
                        <tr key={image.name}>
                          <td className="px-3 py-2">
                            <code className="font-mono text-xs">{image.name}</code>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1">
                              {image.isRunning ? (
                                <Badge className="bg-sev-4/15 text-sev-4 border-0 font-normal">
                                  Running
                                </Badge>
                              ) : null}
                              {image.isDefaultBoot ? (
                                <Badge className="bg-sev-5/15 text-sev-5 border-0 font-normal">
                                  Default boot
                                </Badge>
                              ) : null}
                              {!image.isRunning && !image.isDefaultBoot ? (
                                <Badge variant="secondary" className="font-normal">
                                  Inactive
                                </Badge>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {!image.isDefaultBoot ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setDefaultBootMutation.mutate({
                                    gatewayId: versionGateway.id,
                                    imageName: image.name,
                                  });
                                }}
                                disabled={setDefaultBootMutation.isPending}
                              >
                                {setDefaultBootMutation.isPending
                                  ? 'Saving…'
                                  : 'Set as default'}
                              </Button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}

              {systemImages && systemImages.length > 0 ? (
                <div>
                  <Button
                    variant="outline"
                    onClick={() => { setRebootConfirmGateway(versionGateway); }}
                    disabled={rebootGatewayMutation.isPending}
                    className="gap-1.5 text-sev-3 hover:text-sev-3"
                  >
                    <PowerOff className="w-4 h-4" />
                    Reboot gateway
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">
                    Reboots into the default boot image. If the boot fails repeatedly, the
                    watchdog will roll back automatically.
                  </p>
                </div>
              ) : null}

              <Separator />

              <h4 className="text-sm font-semibold">Download an ISO to your computer</h4>
              <p className="text-xs text-muted-foreground">
                To deploy a version to this gateway, use <strong>Deploy a version</strong> above.
                Download is only for a manual or offline install — it saves the ISO to your
                computer.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="fw-image">ISO image</Label>
                <Select value={selectedFirmwareId} onValueChange={setSelectedFirmwareId}>
                  <SelectTrigger id="fw-image">
                    <SelectValue placeholder="Select an image to download" />
                  </SelectTrigger>
                  <SelectContent>
                    {firmwareImages
                      ?.filter(
                        (img) =>
                          classifyFirmwareFile(img.filename) === 'iso' &&
                          (!versionGateway.architecture ||
                            img.architecture === versionGateway.architecture),
                      )
                      .map((img) => (
                        <SelectItem key={img.id} value={img.id}>
                          {img.version} ({img.architecture})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedFirmwareId ? (
                <Button asChild variant="outline" className="gap-1.5">
                  <a
                    href={withAccount(`${backendApi}/api/firmware-images/${selectedFirmwareId}/download`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Download this ISO to your computer"
                  >
                    <Download className="w-4 h-4" />
                    Download ISO
                  </a>
                </Button>
              ) : null}

                </>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        isActive={!!rebootConfirmGateway}
        title="Confirm reboot"
        message={
          rebootConfirmGateway
            ? `Are you sure you want to reboot ${rebootConfirmGateway.name || '[Unnamed]'}? The gateway will be temporarily offline during the reboot.`
            : ''
        }
        confirmText="Reboot now"
        cancelText="Cancel"
        onConfirm={() => {
          if (rebootConfirmGateway) {
            rebootGatewayMutation.mutate({ gatewayId: rebootConfirmGateway.id });
          }
        }}
        onCancel={() => { setRebootConfirmGateway(null); }}
        isLoading={rebootGatewayMutation.isPending}
      />
    </PageContainer>
  );
}
